import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FlatList, RefreshControl, View } from "react-native";
import { StyleSheet } from "@/utils/appStyleSheet";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import FeedItemRenderer from "@/components/dashboard/FeedItemRenderer";
import UnclaimedMoneyPopup from "@/components/dashboard/UnclaimedMoneyPopup";
import BankOfferPopup from "@/components/dashboard/BankOfferPopup";
import { EmptyState } from "@/components/dashboard/HomeScreenComponents";
import { useFeedData } from "@/hooks/useFeedData";
import { useHomeScreenActions } from "@/hooks/useHomeScreenActions";
import SettingTipSection from "@/components/SettingTipSection";
import useWindow from "@/hooks/useWindow";
import FBTrackingService from "@/services/fbTrackingService";
import { TRACKING_EVENTS } from "@/constants/trackingEvents";
import { storageUtils } from "@/utils/storage";
import { showConfirm } from "@/utils/toast";
import useUserStore from "@/store/user";

export type ClaimTabKey = "all-claims" | "settlements" | "signup-offers";

type Props = {
  selectedTab: ClaimTabKey;
};

const SETTLEMENT_TYPES = ["classAction"];
const SIGNUP_OFFER_TYPES = ["bank", "adgemOffer"];

const ClaimTab: React.FC<Props> = ({ selectedTab }) => {
  const insets = useSafeAreaInsets();
  const { isDesktop } = useWindow();
  const { userProfile } = useUserStore();
  const userId = userProfile?.id || "";

  const [hiddenItemIds, setHiddenItemIds] = useState<string[]>([]);

  // Load hidden items from storage on mount and when userId changes
  useEffect(() => {
    if (userId) {
      const hidden = storageUtils.getHiddenItems(userId);
      setHiddenItemIds(hidden);
    }
  }, [userId]);

  const {
    listFeedToRender,
    expiredClassActions,
    claimedClassActions,
    hasData,
    isLoading,
    refreshAllData,
    refetchFeed,
    suitList,
  } = useFeedData();

  const {
    selectedUnclaimedItem,
    selectedBankOfferItem,
    handleClaim,
    handleUnclaimedPopupClose,
    handleFileClaim,
    handleStartMonitoring,
    handleBankOfferPopupClose,
  } = useHomeScreenActions({ data: suitList }, refetchFeed);

  // Local hide handler that stores in local storage
  const handleHideItem = useCallback(
    async (itemId: string) => {
      if (!userId) return;
      const confirmed = await showConfirm({
        title: "Hide Item",
        message: "Are you sure you want to hide this item?",
        confirmText: "Hide",
        cancelText: "Cancel",
      });
      if (confirmed) {
        storageUtils.addHiddenItem(userId, itemId);
        setHiddenItemIds((prev) => [...prev, itemId]);
      }
    },
    [userId]
  );

  // Track home feed viewed on mount
  useEffect(() => {
    FBTrackingService.trackEvent(TRACKING_EVENTS.HOME_FEED_VIEWED, {
      eventData: {
        has_data: hasData,
        feed_items_count: listFeedToRender.length,
      },
    });
  }, []);

  const allFeedData = [
    ...listFeedToRender,
    ...claimedClassActions,
    ...expiredClassActions,
  ];

  const feedData = useMemo(() => {
    let filtered = allFeedData.filter((item) => {
      const itemId = item?.id || item?._id;
      return !hiddenItemIds.includes(itemId);
    });

    if (selectedTab === "settlements") {
      filtered = filtered.filter((item) =>
        SETTLEMENT_TYPES.includes(item.type),
      );
    } else if (selectedTab === "signup-offers") {
      filtered = filtered.filter((item) =>
        SIGNUP_OFFER_TYPES.includes(item.type),
      );
    }

    return filtered;
  }, [allFeedData, hiddenItemIds, selectedTab]);

  return (
    <>
      <FlatList
        data={feedData}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          isDesktop && styles.contentContainer,
          { paddingBottom: insets.bottom + 100 },
        ]}
        style={styles.listContainer}
        renderItem={({ item }) => (
          <View style={isDesktop && styles.itemContainerDesktop}>
            <FeedItemRenderer
              item={item}
              onHide={handleHideItem}
              onClaim={handleClaim}
              onStartMonitoring={handleStartMonitoring}
            />
          </View>
        )}
        ListEmptyComponent={!hasData ? <EmptyState /> : null}
        ListHeaderComponent={!isDesktop ? <SettingTipSection /> : null}
        refreshControl={
          <RefreshControl
            refreshing={isLoading}
            onRefresh={refreshAllData}
            colors={["#4F46E5"]}
            tintColor="#4F46E5"
          />
        }
      />

      <UnclaimedMoneyPopup
        visible={Boolean(selectedUnclaimedItem)}
        onClose={handleUnclaimedPopupClose}
        onFileClaim={handleFileClaim}
        data={selectedUnclaimedItem}
      />
      <BankOfferPopup
        visible={Boolean(selectedBankOfferItem)}
        onClose={handleBankOfferPopupClose}
        data={selectedBankOfferItem}
      />
    </>
  );
};

export default ClaimTab;

const styles = StyleSheet.create({
  contentContainer: {
    alignItems: "center",
  },
  itemContainerDesktop: {
    width: 600,
  },
  listContainer: {
    width: '100%',
  },
});
